import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  #supportsHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  #pointerStartX = 0;
  #isDragging = false;

  /**
   * Watches the bar width so the marquee keeps enough repeats to fill it.
   * @type {ResizeObserver|undefined}
   */
  #resizeObserver = undefined;

  #resizeFrame = 0;

  /**
   * One pristine copy of each announcement, captured before any track is rewritten.
   * @type {HTMLElement[]}
   */
  #templates = [];

  #tickerActive = false;

  connectedCallback() {
    super.connectedCallback();

    this.#templates = (this.refs.slides ?? []).flatMap((slide) => {
      const item = slide.querySelector('.announcement-bar__text-item');
      return item instanceof HTMLElement ? [/** @type {HTMLElement} */ (item.cloneNode(true))] : [];
    });

    this.#fillMarqueeTracks();

    if (typeof ResizeObserver !== 'undefined') {
      this.#resizeObserver = new ResizeObserver(this.#handleResize);
      this.#resizeObserver.observe(this);
    }

    if (this.#supportsHover) {
      this.addEventListener('mouseenter', this.suspend);
      this.addEventListener('mouseleave', this.resume);
      this.addEventListener('pointerdown', this.#handlePointerDown);
      this.addEventListener('pointerup', this.#handlePointerUp);
      this.addEventListener('pointercancel', this.#handlePointerUp);
      this.addEventListener('click', this.#handleClick, true);

      this.style.touchAction = 'pan-y';
      this.style.userSelect = 'none';
    }
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay || this.#tickerActive) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if ((this.#supportsHover && this.matches(':hover')) || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused || this.#tickerActive) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  #handlePointerDown = (e) => {
    this.#pointerStartX = e.clientX;
    this.#isDragging = false;
    this.setPointerCapture(e.pointerId);
    this.suspend();
  };

  #handlePointerUp = (e) => {
    const deltaX = e.clientX - this.#pointerStartX;
    const threshold = 30;

    if (Math.abs(deltaX) > threshold) {
      this.#isDragging = true;
      if (deltaX < 0) {
        this.next();
      } else {
        this.previous();
      }
    }

    if (this.autoplay && !this.paused) {
      this.suspend();
      this.play();
    }
  };

  #handleClick = (e) => {
    if (this.#isDragging) {
      e.preventDefault();
      e.stopPropagation();
      this.#isDragging = false;
    }
  };

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());

  #handleResize = () => {
    cancelAnimationFrame(this.#resizeFrame);
    this.#resizeFrame = requestAnimationFrame(this.#fillMarqueeTracks);
  };

  /**
   * Rebuilds the marquee tracks for the current breakpoint.
   *
   * Desktop ticker: every announcement runs once, in order, inside the first slide, and that
   * sequence repeats until it fills the bar. Rotation is suspended because the ticker already
   * shows every message.
   *
   * Mobile: each slide keeps its own message and rotation stays on, but the track is still
   * filled — two copies leave a gap at the end of every cycle whenever the text is narrower
   * than the bar.
   */
  #fillMarqueeTracks = () => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const slides = this.refs.slides ?? [];
    const first = slides[0];

    if (!first || !this.#templates.length) return;

    const isTicker =
      window.matchMedia('(min-width: 769px)').matches &&
      Boolean(this.closest('.announcement-bar')?.classList.contains('announcement-bar--marquee-desktop'));

    if (isTicker) {
      this.#tickerActive = true;
      this.suspend();
      this.current = 0;
      this.#fillTrack(first, this.#templates);
      return;
    }

    const wasTicker = this.#tickerActive;

    this.#tickerActive = false;

    slides.forEach((slide, index) => this.#fillTrack(slide, [this.#templates[index]]));

    if (wasTicker) {
      this.current = 0;
      this.play();
    }
  };

  /**
   * Repeats `templates` inside one slide until half the track can cover the bar on its own.
   *
   * Each cycle scrolls exactly half the track past the viewport, so the remaining half is what
   * the shopper sees at the moment the loop restarts. If that half is narrower than the bar,
   * the tail of the last message is followed by empty space before the first one comes back.
   *
   * @param {Element} slide
   * @param {(HTMLElement|undefined)[]} templates
   */
  #fillTrack(slide, templates) {
    const track = slide.querySelector('.announcement-bar__text-track');
    const viewport = track?.parentElement;

    if (!track || !viewport) return;

    // Measure one pass, so re-running on resize stays idempotent.
    this.#renderTrack(track, templates, 1);

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const sequence = track.getBoundingClientRect().width;
    const width = viewport.clientWidth;

    if (sequence <= 0 || width <= 0) return;

    const repeats = Math.max(1, Math.ceil((width + gap) / (sequence + gap)));

    this.#renderTrack(track, templates, repeats * 2);

    // The keyframe covers half the track, so the duration must scale with what half holds
    // to keep a constant scroll speed. The setting reads as seconds per message.
    const seconds = parseFloat(getComputedStyle(track).getPropertyValue('--announcement-bar-marquee-speed'));

    if (!Number.isNaN(seconds)) {
      track.style.setProperty('--announcement-bar-marquee-duration', `${seconds * templates.length * repeats}s`);
    }
  }

  /**
   * Fills a track with `copies` passes over `templates`.
   *
   * @param {Element} track
   * @param {(HTMLElement|undefined)[]} templates
   * @param {number} copies
   */
  #renderTrack(track, templates, copies) {
    /** @type {HTMLElement[]} */
    const items = [];

    for (let pass = 0; pass < copies; pass++) {
      for (const template of templates) {
        if (!template) continue;

        const item = /** @type {HTMLElement} */ (template.cloneNode(true));

        // Only the first pass is exposed; the repeats are decorative.
        if (pass === 0) item.removeAttribute('aria-hidden');
        else item.setAttribute('aria-hidden', 'true');

        items.push(item);
      }
    }

    if (items.length) track.replaceChildren(...items);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();

    cancelAnimationFrame(this.#resizeFrame);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;
    this.suspend();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
